/** Shared fleet UI bits: formatting, status pills, image capture, signature pad and the route map. */
import { formatGBP, UPLOAD_TOO_BIG, uploadTooBig } from '@tradeoutlet/shared';
import { STATUS_TONE } from '@/lib/status';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import type { ComplianceState, FleetConfig, JourneyStatus, JourneyStop, LatLng, Vehicle } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { fmt, sentence } from '@/lib/format';
import { useWorkspaceId } from '@/lib/hooks';

export const fleetKey = (ws: string | null, ...rest: unknown[]) => ['fleet', ws, ...rest];
export function useFleetConfig() {
  const ws = useWorkspaceId();
  return useQuery({ queryKey: fleetKey(ws, 'config'), queryFn: () => api.fleet.config(ws!), enabled: Boolean(ws), staleTime: 10 * 60_000 });
}

export const hhmm = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '–');
export const dayTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '–');
export const miles = (m: number | null | undefined) => (m == null ? '–' : `${(m / 1609.344).toFixed(1)} mi`);
export const dur = (s: number | null | undefined) => (s == null ? '–' : s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m` : `${Math.round(s / 60)} min`);
export const gbp = (p: number | null | undefined) => formatGBP(p, { empty: '–' });
export const fmtReg = (r: string | null | undefined) => (r ? (r.length === 7 ? `${r.slice(0, 4)} ${r.slice(4)}` : r) : '–');
export const VEHICLE_LABEL: Record<Vehicle['vehicle_type'], string> = { car: 'Car', van: 'Van', luton: 'Luton van', truck_7_5t: '7.5t truck', hgv: 'HGV' };

const JS: Record<JourneyStatus, string> = {
  // Same tones as every other status badge (lib/status): planned = draft, assigned = waiting, in progress = open, completed = done, cancelled = ended.
  planned: STATUS_TONE.draft, assigned: STATUS_TONE.pending, in_progress: STATUS_TONE.submitted, completed: STATUS_TONE.approved, cancelled: STATUS_TONE.ended,
};
export function JourneyStatusPill({ s }: { s: JourneyStatus }) {
  return <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-medium', JS[s])}>{sentence(s)}</span>;
}
const CS: Record<ComplianceState, string> = {
  ok: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200', due_soon: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
  overdue: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200', not_set: 'bg-slate-100 text-slate-500 dark:bg-white/10', not_required: 'hidden',
};
export function ComplianceChips({ v }: { v: Vehicle }) {
  return (
    <span className="flex flex-wrap gap-1">
      {(v.compliance ?? []).filter(c => c.state !== 'not_required').map(c => (
        <span key={c.item} title={c.due ? `Due ${fmt.date(c.due)}` : 'Not set'} className={clsx('rounded px-1.5 py-0.5 text-[11px] font-medium uppercase', CS[c.state])}>
          {c.item}{c.state === 'overdue' ? ' overdue' : c.state === 'due_soon' && c.days_left != null ? ` ${c.days_left}d` : ''}
        </span>
      ))}
    </span>
  );
}

/**
 * Downscale a photo to a JPEG data URL small enough for the 700 KB fleet upload (PDFs go as they are). t364u: a file
 * over the 50 MB upload limit is refused first, with the same message as every other upload.
 */
export async function imageToDataUrl(file: File, max = 1400): Promise<string> {
  if (uploadTooBig(file.size)) throw new Error(UPLOAD_TOO_BIG);
  if (file.type === 'application/pdf') return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = rej; r.readAsDataURL(file); });
  const bmp = await createImageBitmap(file);
  const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * k); c.height = Math.round(bmp.height * k);
  c.getContext('2d')!.drawImage(bmp, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', 0.72);
}

/** Photo picker that uploads as it goes; returns upload ids. Uses the camera on phones. */
export function PhotoInput({ upload, ids, onChange, max = 6, label = 'Add photo' }: { upload: (dataUrl: string) => Promise<{ id: string }>; ids: string[]; onChange: (ids: string[]) => void; max?: number; label?: string }) {
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState<string[]>([]);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {thumbs.map((t, i) => <img key={i} src={t} alt="" className="h-14 w-14 rounded object-cover ring-1 ring-black/10" />)}
        {ids.length < max && (
          <label className={clsx('flex h-14 cursor-pointer items-center rounded border border-dashed border-slate-300 px-3 text-xs text-slate-600 dark:border-white/20 dark:text-slate-300', busy && 'opacity-50')}>
            {busy ? 'Uploading…' : label}
            <input type="file" accept="image/*" capture="environment" className="hidden" disabled={busy} data-testid="photo-input" onChange={async e => {
              const f = e.target.files?.[0]; e.target.value = '';
              if (!f) return;
              setBusy(true); setErr(null);
              try { const d = await imageToDataUrl(f); const r = await upload(d); setThumbs(t => [...t, d]); onChange([...ids, r.id]); } catch (x) { setErr((x as Error).message || 'Upload failed'); }
              setBusy(false);
            }} />
          </label>
        )}
      </div>
      {err && <div className="mt-1 text-xs text-rose-600">{err}</div>}
    </div>
  );
}

/** Finger/mouse signature pad; onChange gets a PNG data URL (or null when cleared). */
export function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [has, setHas] = useState(false);
  const pos = (e: React.PointerEvent) => { const r = ref.current!.getBoundingClientRect(); return [(e.clientX - r.left) * (ref.current!.width / r.width), (e.clientY - r.top) * (ref.current!.height / r.height)]; };
  return (
    <div>
      <canvas ref={ref} width={600} height={200} data-testid="signature-pad" className="h-32 w-full touch-none rounded border border-slate-300 bg-white dark:border-white/20"
        onPointerDown={e => { drawing.current = true; const c = ref.current!.getContext('2d')!; c.lineWidth = 3; c.lineCap = 'round'; c.strokeStyle = '#111'; const [x, y] = pos(e); c.beginPath(); c.moveTo(x, y); (e.target as Element).setPointerCapture(e.pointerId); }}
        onPointerMove={e => { if (!drawing.current) return; const c = ref.current!.getContext('2d')!; const [x, y] = pos(e); c.lineTo(x, y); c.stroke(); }}
        onPointerUp={() => { if (!drawing.current) return; drawing.current = false; setHas(true); onChange(ref.current!.toDataURL('image/png')); }} />
      <div className="mt-1 flex justify-between text-xs text-slate-500">
        <span>Sign above</span>
        {has && <button type="button" className="underline" onClick={() => { ref.current!.getContext('2d')!.clearRect(0, 0, 600, 200); setHas(false); onChange(null); }}>Clear</button>}
      </div>
    </div>
  );
}

/** Google Maps directions link for a stop - opens the native app on phones. */
export const navUrl = (s: Pick<JourneyStop, 'lat' | 'lng' | 'address' | 'label'>) =>
  `https://www.google.com/maps/dir/?api=1&travelmode=driving&destination=${encodeURIComponent(s.lat != null && s.lng != null ? `${s.lat},${s.lng}` : s.address ?? s.label)}`;
export const wazeUrl = (s: Pick<JourneyStop, 'lat' | 'lng'>) => (s.lat != null && s.lng != null ? `https://waze.com/ul?ll=${s.lat},${s.lng}&navigate=yes` : null);

// ---------------------------------------------------------------------------------------------- map
type Pin = LatLng & { label: string; kind: 'origin' | 'stop' | 'done' | 'late' | 'truck' };
declare global { interface Window { google?: any; __fleetMapsLoading?: Promise<void> } } // eslint-disable-line @typescript-eslint/no-explicit-any

export function loadMaps(key: string): Promise<void> {
  if (window.google?.maps) return Promise.resolve();
  window.__fleetMapsLoading ??= new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly`;
    s.async = true; s.onload = () => res(); s.onerror = () => rej(new Error('Maps failed to load'));
    document.head.appendChild(s);
  });
  return window.__fleetMapsLoading;
}

/**
 * Route map. With a browser-restricted Maps JS key (GOOGLE_MAPS_BROWSER_KEY) it draws a real Google map; otherwise
 * (mock provider, local dev, tests) a schematic SVG of the same pins and polyline so planning still works offline.
 */
export function RouteMap({ pins, line, trail, height = 280, onPick }: { pins: Pin[]; line?: LatLng[] | null; /** t541u: where the vehicle went (solid blue). */ trail?: LatLng[] | null; height?: number; onPick?: (p: LatLng) => void }) {
  const cfg = useFleetConfig().data as FleetConfig | undefined;
  const key = cfg?.browser_maps_key ?? null;
  const [failed, setFailed] = useState(false);
  if (key && !failed) return <GoogleMap apiKey={key} pins={pins} line={line} trail={trail} height={height} onFail={() => setFailed(true)} onPick={onPick} />;
  return <SchematicMap pins={pins} line={line} trail={trail} height={height} onPick={onPick} />;
}

function GoogleMap({ apiKey, pins, line, trail, height, onFail, onPick }: { apiKey: string; pins: Pin[]; line?: LatLng[] | null; trail?: LatLng[] | null; height: number; onFail: () => void; onPick?: (p: LatLng) => void }) {
  const el = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let live = true;
    loadMaps(apiKey).then(() => {
      if (!live || !el.current) return;
      const g = window.google.maps;
      const map = new g.Map(el.current, { disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative' });
      const b = new g.LatLngBounds();
      pins.forEach((p, i) => { b.extend(p); new g.Marker({ map, position: p, label: p.kind === 'origin' ? 'W' : p.kind === 'truck' ? '🚚' : String(i), title: p.label }); });
      if (line?.length) { new g.Polyline({ map, path: line, strokeColor: '#111827', strokeWeight: 4, strokeOpacity: 0.8 }); line.forEach(p => b.extend(p)); }
      if (trail && trail.length > 1) { new g.Polyline({ map, path: trail, strokeColor: TRAIL_COLOUR, strokeWeight: 4, strokeOpacity: 0.85 }); trail.forEach(p => b.extend(p)); }
      if (!b.isEmpty()) map.fitBounds(b, 40);
      if (onPick) map.addListener('click', (e: { latLng: { lat: () => number; lng: () => number } }) => onPick({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
    }).catch(onFail);
    return () => { live = false; };
  }, [apiKey, JSON.stringify(pins), JSON.stringify(line), JSON.stringify(trail)]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={el} style={{ height }} className="w-full overflow-hidden rounded-lg bg-slate-100" data-testid="google-map" />;
}

/** t541u: the colour of a vehicle's trail (where it has been) on every fleet map. */
export const TRAIL_COLOUR = '#0369a1';
const COLORS: Record<Pin['kind'], string> = { origin: '#111827', stop: '#2563eb', done: '#059669', late: '#dc2626', truck: '#f5c400' };
function SchematicMap({ pins, line, trail, height, onPick }: { pins: Pin[]; line?: LatLng[] | null; trail?: LatLng[] | null; height: number; onPick?: (p: LatLng) => void }) {
  const W = 600;
  const pts = useMemo(() => [...pins, ...(line ?? []), ...(trail ?? [])], [pins, line, trail]);
  if (!pts.length) return <div style={{ height }} className="flex items-center justify-center rounded-lg bg-slate-100 text-sm text-slate-500 dark:bg-white/5">Add stops to see the route</div>;
  const lats = pts.map(p => p.lat), lngs = pts.map(p => p.lng);
  const pad = 0.02;
  const [minLa, maxLa, minLn, maxLn] = [Math.min(...lats) - pad, Math.max(...lats) + pad, Math.min(...lngs) - pad, Math.max(...lngs) + pad];
  const kx = Math.cos(((minLa + maxLa) / 2) * Math.PI / 180);
  const sx = (W - 40) / Math.max(1e-6, (maxLn - minLn) * kx), sy = (height - 40) / Math.max(1e-6, maxLa - minLa);
  const s = Math.min(sx, sy);
  const x = (ln: number) => 20 + (ln - minLn) * kx * s + ((W - 40) - (maxLn - minLn) * kx * s) / 2;
  const y = (la: number) => 20 + (maxLa - la) * s + ((height - 40) - (maxLa - minLa) * s) / 2;
  const path = (line?.length ? line : pins.filter(p => p.kind !== 'truck')).map((p, i) => `${i ? 'L' : 'M'}${x(p.lng).toFixed(1)},${y(p.lat).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{ height }} className={clsx('w-full rounded-lg bg-[#eef2f0] dark:bg-white/5', onPick && 'cursor-crosshair')} data-testid="schematic-map"
      onClick={onPick ? e => { const r = (e.currentTarget as SVGElement).getBoundingClientRect(); const px = (e.clientX - r.left) * (W / r.width), py = (e.clientY - r.top) * (height / r.height);
        const off = ((W - 40) - (maxLn - minLn) * kx * s) / 2, offy = ((height - 40) - (maxLa - minLa) * s) / 2;
        onPick({ lat: maxLa - (py - 20 - offy) / s, lng: minLn + (px - 20 - off) / (kx * s) }); } : undefined}>
      <defs><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M40 0H0V40" fill="none" stroke="#000" strokeOpacity="0.04" /></pattern></defs>
      <rect width={W} height={height} fill="url(#grid)" />
      <path d={path} fill="none" stroke="#111827" strokeOpacity={line?.length ? 0.75 : 0.35} strokeWidth={line?.length ? 3 : 2} strokeDasharray={line?.length ? undefined : '6 5'} />
      {trail && trail.length > 1 && <path d={trail.map((p, i) => `${i ? 'L' : 'M'}${x(p.lng).toFixed(1)},${y(p.lat).toFixed(1)}`).join(' ')} fill="none" stroke={TRAIL_COLOUR} strokeOpacity={0.85} strokeWidth={3} strokeLinejoin="round" data-testid="map-trail" />}
      {pins.map((p, i) => (
        <g key={i} transform={`translate(${x(p.lng)},${y(p.lat)})`}>
          <circle r={p.kind === 'truck' ? 9 : 11} fill={COLORS[p.kind]} stroke="#fff" strokeWidth="2" />
          <text textAnchor="middle" dy="4" fontSize="11" fontWeight="700" fill={p.kind === 'truck' ? '#000' : '#fff'}>{p.kind === 'origin' ? 'W' : p.kind === 'truck' ? '▶' : i}</text>
          <text x="14" dy="4" fontSize="11" fill="currentColor" className="fill-slate-700 dark:fill-slate-200">{p.label}</text>
        </g>
      ))}
      <text x={W - 8} y={height - 8} textAnchor="end" fontSize="10" className="fill-slate-400">Schematic map (no Maps key)</text>
    </svg>
  );
}

export function stopPins(origin: (LatLng & { name: string }) | null, stops: JourneyStop[], truck?: LatLng | null): Pin[] {
  const pins: Pin[] = [];
  if (origin) pins.push({ ...origin, label: origin.name, kind: 'origin' });
  for (const s of stops) if (s.lat != null && s.lng != null) pins.push({ lat: s.lat, lng: s.lng, label: s.label, kind: s.status === 'completed' ? 'done' : s.status === 'failed' || s.late ? 'late' : 'stop' }); // failed shows red, like late
  if (truck) pins.push({ ...truck, label: 'Driver', kind: 'truck' });
  return pins;
}

/**
 * Geofence picker (UI review 2.2): a centre pin and the radius drawn to scale. Click or tap to move the centre.
 * Uses Google Maps when a browser key is configured, otherwise a scaled schematic (no tiles, works offline).
 */
export function FenceMap({ centre, radius, onPick, height = 220 }: { centre: LatLng | null; radius: number; onPick: (p: LatLng) => void; height?: number }) {
  const cfg = useFleetConfig().data as FleetConfig | undefined;
  const key = cfg?.browser_maps_key ?? null;
  const [failed, setFailed] = useState(false);
  if (!centre) return <div style={{ height }} className="flex items-center justify-center rounded-lg bg-slate-100 px-4 text-center text-sm text-slate-600 dark:bg-white/5 dark:text-slate-300">Set a position (or use your current one) to see the fence</div>;
  if (key && !failed) return <GoogleFence apiKey={key} centre={centre} radius={radius} onPick={onPick} height={height} onFail={() => setFailed(true)} />;
  // Schematic: view spans 3× the radius each side; metres → px with a flat-earth approximation (fine at fence scale).
  const W = 600, span = Math.max(radius * 3, 150), mPerPx = (2 * span) / height;
  const r = radius / mPerPx;
  return (
    <svg viewBox={`0 0 ${W} ${height}`} style={{ height }} className="w-full cursor-crosshair rounded-lg bg-[#eef2f0] dark:bg-white/5" data-testid="fence-map" role="img"
      aria-label={`Geofence: ${radius} m radius around ${centre.lat.toFixed(5)}, ${centre.lng.toFixed(5)}. Click to move the centre.`}
      onClick={e => {
        const b = (e.currentTarget as SVGElement).getBoundingClientRect();
        const dx = ((e.clientX - b.left) * (W / b.width) - W / 2) * mPerPx, dy = ((e.clientY - b.top) * (height / b.height) - height / 2) * mPerPx;
        onPick({ lat: centre.lat - dy / 111_320, lng: centre.lng + dx / (111_320 * Math.cos(centre.lat * Math.PI / 180)) });
      }}>
      <defs><pattern id="fgrid" width="30" height="30" patternUnits="userSpaceOnUse"><path d="M30 0H0V30" fill="none" stroke="#000" strokeOpacity="0.05" /></pattern></defs>
      <rect width={W} height={height} fill="url(#fgrid)" />
      <circle cx={W / 2} cy={height / 2} r={r} fill="#111827" fillOpacity="0.08" stroke="#111827" strokeWidth="2" strokeDasharray="6 4" />
      <circle cx={W / 2} cy={height / 2} r="7" fill="#111827" stroke="#fff" strokeWidth="2" />
      <line x1={16} x2={16 + 100 / mPerPx} y1={height - 14} y2={height - 14} stroke="#111827" strokeWidth="2" />
      <text x={16} y={height - 20} fontSize="10" className="fill-slate-600">100 m</text>
      <text x={W - 8} y={height - 8} textAnchor="end" fontSize="10" className="fill-slate-600">Schematic (no Maps key) · click to move</text>
    </svg>
  );
}

function GoogleFence({ apiKey, centre, radius, onPick, height, onFail }: { apiKey: string; centre: LatLng; radius: number; onPick: (p: LatLng) => void; height: number; onFail: () => void }) {
  const el = useRef<HTMLDivElement>(null);
  const pick = useRef(onPick); pick.current = onPick;
  useEffect(() => {
    let live = true;
    loadMaps(apiKey).then(() => {
      if (!live || !el.current) return;
      const g = window.google.maps;
      const map = new g.Map(el.current, { center: centre, disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative' });
      new g.Marker({ map, position: centre });
      const circle = new g.Circle({ map, center: centre, radius, strokeColor: '#111827', strokeWeight: 2, fillColor: '#111827', fillOpacity: 0.08, clickable: false });
      map.fitBounds(circle.getBounds(), 30);
      map.addListener('click', (e: { latLng: { lat: () => number; lng: () => number } }) => pick.current({ lat: e.latLng.lat(), lng: e.latLng.lng() }));
    }).catch(onFail);
    return () => { live = false; };
  }, [apiKey, centre.lat, centre.lng, radius]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div ref={el} style={{ height }} className="w-full overflow-hidden rounded-lg bg-slate-100" data-testid="google-fence-map" />;
}
